import mParticlem from '@mparticle/web-sdk';
import type {
  IdentityResult,
  MParticleWebSDK,
  SDKEventCustomFlags,
} from '@mparticle/web-sdk';
import { IErrorLogMessage } from '@mparticle/web-sdk/dist/types/src/mp-instance';
// https://github.com/mParticle/mparticle-web-sdk/blob/v2.64.0/dist/types/src/public-types.d.ts#L10
import type { SDKEventAttrs } from 'mparticle__web-sdk';

import getConfig from '@/appconfig/configUtils';
import { MaturityLevels } from '@/enums';
import { notifyComscoreViewEvent } from '@/services/comscore';
import { ScreenName } from '@/services/customerDataSuite/constants';
import { Maturity } from '@/services/graphQL/api/generated/types_web';
import { AccountDetails, ShelfTrackParams } from '@/types';
import { isDev, isProd } from '@/utils/enviroment';

import {
  AppAttributes,
  PageType,
  PlatformType,
  ProfileType,
  UserAttributes,
  UserIdentities,
} from './constants';

const PROD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;

// temporary fix, because mParticle is not typed correctly on version 2.64.1 of the mParticle web SDK
const mParticle = mParticlem as MParticleWebSDK;

function identityReceived(result: IdentityResult): void {
  console.log('mParticle - identityReceived:', result);
}

export function init(config: any): void {
  const mParticleIsEnabled = !!config?.featureFlags?.mParticle;

  if (!mParticleIsEnabled) return;

  const mParticleConfig = {
    isDevelopmentMode: PROD_ENV !== 'prod' ? true : false,
    dataPlan: {
      planId: config?.analytics?.mParticle?.planId,
      planVersion: config?.analytics?.mParticle?.planVersion,
    },
  };

  mParticle.init(config?.analytics?.mParticle?.appID, mParticleConfig);
}

/* Customer Identity Requests */

export function identifyUser(customerid: string) {
  if (!mParticle.isInitialized()) return;

  var identityRequest = {
    userIdentities: {
      customerid,
    },
  };

  mParticle.Identity.identify?.(identityRequest, identityReceived);
}

export function loginUser(customerid: string, email?: string) {
  if (!mParticle.isInitialized()) return;

  const userIdentities = {
    customerid,
    ...(email ? { email } : {}),
  };

  var identityRequest = {
    userIdentities,
  };
  mParticle.Identity.login?.(identityRequest, identityReceived);
}

export function logoutUser() {
  if (!mParticle.isInitialized()) return;

  mParticle.Identity.logout?.({}, identityReceived);
}

// TODO: Map the attrs and flags to generic types.
export function pageView(page: string, attrs: SDKEventAttrs, flags: SDKEventCustomFlags) {
  if (!mParticle.isInitialized()) return;

  console.log('mParticle - Page View: ', page, attrs, flags);
  mParticle.logPageView(page, attrs, flags);
  notifyComscoreViewEvent((attrs as any)?.title ?? '');
}

// TODO: Map the attrs and flags to generic types.
export function logEvent(event: any, attrs?: SDKEventAttrs) {
  if (!mParticle.isInitialized()) return;

  console.log('mParticle - Event: ', event, 'Attributes:', attrs);
  mParticle.logEvent(event[0], event[1], attrs);
}

export function logError(error: string, attrs: SDKEventAttrs) {
  if (!mParticle.isInitialized()) return;

  console.log('mParticle - Error: ', error, attrs);
  mParticle.logError({ message: error } as IErrorLogMessage, attrs);
}

/* User Attributes Tracking Requests */

export async function setDeviceAttributes() {
  if (!mParticle.isInitialized()) return;

  console.log('mParticle - setDeviceAttributes');
  const config = await getConfig();
  mParticle.setAppName(config?.getAppName());
  mParticle.setAppVersion(config?.getAppVersion());
  mParticle.setDeviceId(await AppAttributes.DEVICE_ID);
}

export function setUserAttributes(accountDetails: AccountDetails) {
  if (!mParticle.isInitialized()) return;

  console.log('mParticle - setUserAttributes:', accountDetails);
  const currentUser = mParticle?.Identity?.getCurrentUser?.();
  if (currentUser) {
    currentUser.setUserAttribute(UserIdentities.CUSTOMER_ID, accountDetails.id);
    if (accountDetails.email) {
      currentUser.setUserAttribute(UserIdentities.EMAIL, accountDetails.email);
    }
    if (accountDetails.phoneNumber) {
      currentUser.setUserAttribute(
        UserIdentities.MOBILE_NUMBER,
        accountDetails.phoneNumber
      );
    }
    currentUser.setUserAttribute(
      UserAttributes.CUSTOMER_TYPE,
      accountDetails.bdu ? 'bdu' : 'dtc'
    );
  }
}

export function trackShelfData(shelfTrackData: ShelfTrackParams) {
  if (typeof window === 'undefined') return;

  window.digitalData = window.digitalData || {};
  window.digitalData.shelfTrack = {
    containerId: shelfTrackData.containerId,
    containerDisplayTitle: shelfTrackData.containerDisplayTitle,
    containerParentPageName: shelfTrackData.containerParentPageName,
    containerPositionVertical: shelfTrackData.containerPositionVertical,
    containerPositionHorizontal: shelfTrackData.containerPositionHorizontal,
    parentPageType: shelfTrackData.parentPageType,
  };
}

// Helper functions

export const getPlatformId = (id: string) => {
  switch (id) {
    case 'hisense':
      return PlatformType.TV_Hisense;
    case 'lg':
      return PlatformType.TV_LG_WebOS;
    case 'samsungtv':
      return PlatformType.TV_Samsung;
    case 'xbox':
      return PlatformType.XBOX_X;
    case 'ps5':
      return PlatformType.TV_PS5;
    case 'xfinity':
      return PlatformType.STB_Xfinity;
    default:
      return PlatformType.Web;
  }
};

export const getParentPageType = (parentPage: string | undefined) => {
  switch (parentPage) {
    case 'LANDING_PAGE':
      return PageType.LandingPage;
    case 'SHOW_PAGE':
    case ScreenName.SHOW_PAGE:
      return PageType.MediaPage;
    case 'LIVE_PAGE':
      return PageType.LivePage;
    case 'ARTICLE':
      return PageType.Article;
    case 'INDEX':
      return PageType.Index;
    case 'RECIPE':
      return PageType.Recipe;
    default:
      return PageType.LandingPage;
  }
};

export const getProfileType = (maturity: Maturity | MaturityLevels) => {
  switch (maturity) {
    case MaturityLevels.ADULT:
      return ProfileType.Adult;
    case MaturityLevels.OLDER_KID:
      return ProfileType.OlderKid;
    case MaturityLevels.PRE_SCHOOL:
      return ProfileType.Preschool;
    case MaturityLevels.TEEN:
      return ProfileType.Teen;
    case MaturityLevels.YOUNGER_KID:
      return ProfileType.YoungetKid;
    default:
      return ProfileType.Adult;
  }
};

/**
 * Returns the Firebolt AgePolicy tag for a given profile maturity level.
 * Must be passed to all Firebolt Metrics method calls (except appInfo) when
 * the data originates from a child profile, per platform child-directed data requirements.
 */
export const getAgePolicy = (
  maturity: Maturity | null | undefined
): 'app:adult' | 'app:child' | 'app:teen' => {
  switch (maturity) {
    case Maturity.Teen:
      return 'app:teen';
    case Maturity.KidO:
    case Maturity.KidY:
    case Maturity.KidPre:
      return 'app:child';
    default:
      return 'app:adult';
  }
};
